import { isLocale, type Locale } from "@/paraglide/runtime";

export type AppLocale = Locale;

export const APP_LOCALES = [
  "en",
  "es",
  "de",
  "zh",
  "ar",
  "fr",
  "vi",
  "sw",
  "th",
  "bs",
  "ja",
  "ko",
  "pt",
  "it",
  "sv",
  "nl",
  "da",
  "nb",
  "fi",
  "pl",
  "ru",
  "tr",
  "hi",
  "id",
  "ms",
  "cs",
  "el",
  "he",
  "af",
  "hu",
] as const satisfies readonly AppLocale[];

export const LOCALE_SHORT_LABEL: Record<AppLocale, string> = {
  en: "EN",
  es: "ES",
  de: "DE",
  zh: "ZH",
  ar: "AR",
  fr: "FR",
  vi: "VI",
  sw: "SW",
  th: "TH",
  bs: "BS",
  ja: "JA",
  ko: "KO",
  pt: "PT",
  it: "IT",
  sv: "SV",
  nl: "NL",
  da: "DA",
  nb: "NB",
  fi: "FI",
  pl: "PL",
  ru: "RU",
  tr: "TR",
  hi: "HI",
  id: "ID",
  ms: "MS",
  cs: "CS",
  el: "EL",
  he: "HE",
  af: "AF",
  hu: "HU",
};

export function toAppLocale(value: unknown): AppLocale | null {
  if (typeof value !== "string") return null;
  const normalized = value.trim().toLowerCase();
  if (isLocale(normalized)) return normalized;
  // Longer / ambiguous prefixes first where needed (nb before n*, zh, etc.)
  if (normalized.startsWith("nb") || normalized.startsWith("nn") || normalized === "no")
    return "nb";
  if (normalized.startsWith("zh")) return "zh";
  if (normalized.startsWith("es")) return "es";
  if (normalized.startsWith("en")) return "en";
  if (normalized.startsWith("de")) return "de";
  if (normalized.startsWith("ar")) return "ar";
  if (normalized.startsWith("fr")) return "fr";
  if (normalized.startsWith("vi")) return "vi";
  if (normalized.startsWith("sw")) return "sw";
  if (normalized.startsWith("th")) return "th";
  if (normalized.startsWith("bs")) return "bs";
  if (normalized.startsWith("ja")) return "ja";
  if (normalized.startsWith("ko")) return "ko";
  if (normalized.startsWith("pt")) return "pt";
  if (normalized.startsWith("it")) return "it";
  if (normalized.startsWith("sv")) return "sv";
  if (normalized.startsWith("nl")) return "nl";
  if (normalized.startsWith("da")) return "da";
  if (normalized.startsWith("fi")) return "fi";
  if (normalized.startsWith("pl")) return "pl";
  if (normalized.startsWith("ru")) return "ru";
  if (normalized.startsWith("tr")) return "tr";
  if (normalized.startsWith("hi")) return "hi";
  if (normalized.startsWith("id") || normalized.startsWith("in")) return "id";
  if (normalized.startsWith("ms")) return "ms";
  if (normalized.startsWith("cs")) return "cs";
  if (normalized.startsWith("el")) return "el";
  if (normalized.startsWith("he") || normalized.startsWith("iw")) return "he";
  if (normalized.startsWith("af")) return "af";
  if (normalized.startsWith("hu")) return "hu";
  return null;
}

/** BCP 47 tag for Intl date/number formatting. */
export function intlLocaleFor(locale: AppLocale): string {
  switch (locale) {
    case "es":
      return "es-US";
    case "de":
      return "de-DE";
    case "zh":
      return "zh-CN";
    case "bs":
      // Chromium (Chrome/Edge) ships no bs date data: "bs-BA" formats with the root
      // locale («M10 10, Sat»). sr-Latn-BA is Latin script with Bosnian-compatible weekdays
      // and most month names (oktobar, not Croatian listopad) and works in Chromium, Node,
      // Firefox and Safari; its Serbian jun/jul/avgust are mapped by `bosnianLongMonths`.
      // Always use it (not a runtime probe) so SSR and client markup stay identical.
      return "sr-Latn-BA";
    case "ar":
      return "ar";
    case "fr":
      return "fr-FR";
    case "vi":
      return "vi-VN";
    case "sw":
      return "sw-KE";
    case "th":
      return "th-TH";
    case "ja":
      return "ja-JP";
    case "ko":
      return "ko-KR";
    case "pt":
      return "pt-BR";
    case "it":
      return "it-IT";
    case "sv":
      return "sv-SE";
    case "nl":
      return "nl-NL";
    case "da":
      return "da-DK";
    case "nb":
      return "nb-NO";
    case "fi":
      return "fi-FI";
    case "pl":
      return "pl-PL";
    case "ru":
      return "ru-RU";
    case "tr":
      return "tr-TR";
    case "hi":
      return "hi-IN";
    case "id":
      return "id-ID";
    case "ms":
      return "ms-MY";
    case "cs":
      return "cs-CZ";
    case "el":
      return "el-GR";
    case "he":
      return "he-IL";
    case "af":
      return "af-ZA";
    case "hu":
      return "hu-HU";
    default:
      return "en-US";
  }
}

/**
 * `lang` for admin-authored DB text that is stored only in English (event titles, audience,
 * details). Marking it `lang="en"` lets screen readers pronounce it as English and makes the
 * site-wide CSS `text-transform: uppercase` on headings use English casing rules, so tr pages
 * render "SPRING", not the Turkish dotted "SPRİNG". Undefined under en (the page lang applies).
 */
export function englishOnlyContentLang(locale: string): "en" | undefined {
  return locale === "en" ? undefined : "en";
}

/**
 * sr-Latn-BA (the Intl stand-in for bs, see `intlLocaleFor`) spells three long month names
 * the Serbian way. Bosnian uses juni/juli/august. Apply to long-month output only: the short
 * forms (jun, jul) are the same in both languages.
 */
export function bosnianLongMonths(text: string): string {
  return text
    .replace(/(?<!\p{L})jun(?!\p{L})/gu, "juni")
    .replace(/(?<!\p{L})jul(?!\p{L})/gu, "juli")
    .replace(/(?<!\p{L})avgust(?!\p{L})/gu, "august");
}
